import { ExpandMore } from '@mui/icons-material';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Select as MuiSelect,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import { User } from 'api/models/User';
import notifyPopup from 'components/Common/Notification/notifyPopup';
import MuiPhoneForm from 'components/Common/Phone/muiPhoneForm';
import SectionHeader from 'components/Common/SectionHeader';
import Form from 'components/RouteWizard/Form';
import Input from 'components/RouteWizard/Input';
import useUserProperties from 'hooks/useUserProperties';
import useUserRole from 'hooks/useUserRole';
import React, { useRef } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import Select from 'react-select';
import { updateUserById } from 'reducers/usersReducer';
import { updateUserSchema } from './schema';

interface UpdateUserProps {
  closeDrawer: () => void
  client: any
  user: User
  setDirtyDrawer: (dirty: boolean) => void
}

type TravelPolicyOption = { value: number | null; label: string }

enum travelPolicySpendAttributes {
  spendLimitPerJob = 'Spend Limit Per Job',
  spendLimitPerDay = 'Spend Limit Per Day',
  spendLimitPerWeek = 'Spend Limit Per Week',
  spendLimitPerMonth = 'Spend Limit Per Month',
  groupSpendLimitPerDay = 'Client Spend Limit Per Day',
  groupSpendLimitPerWeek = 'Client Spend Limit Per Week',
  groupSpendLimitPerMonth = 'Client Spend Limit Per Month',
}

const areValuesEqual = (a: any, b: any) => {
  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false
    const sortedA = [...a].sort()
    const sortedB = [...b].sort()
    return sortedA.every((val, idx) => val === sortedB[idx])
  }
  return (a ?? null) === (b ?? null)
}

const mapDelegatesForPatch = (delegates: any[] = []) =>
  delegates.map((delegate: any) => {
    const normalizedDelegate: any = {
      permissionTypeName: delegate.permissionTypeName,
      delegateId: delegate.delegate?.delegateId ?? delegate.delegateId,
    }

    if (delegate.startDateTime !== undefined) {
      normalizedDelegate.startDateTime = delegate.startDateTime
    }
    if (delegate.endDateTime !== undefined) {
      normalizedDelegate.endDateTime = delegate.endDateTime
    }

    return normalizedDelegate
  })

const UpdateUser = (props: UpdateUserProps) => {
  const formMethods = useRef<UseFormReturn | null>(null)
  const { filteredRoles } = useUserRole()
  const [submitPressed, setSubmitPressed] = React.useState(false)
  const [roleError, setRoleError] = React.useState<string | null>(null)
  const dispatch = useDispatch()
  const { userProperties } = useUserProperties(props.client.id)

  const initialUserProps = React.useMemo(() => {
    const map: Record<number, string> = {}
    ;(props.user.userProperties || []).forEach((p: any) => {
      if (typeof p?.userPropertyId === 'number') {
        map[p.userPropertyId] = p.value ?? ''
      }
    })
    return map
  }, [props.user])

  const [userPropValues, setUserPropValues] = React.useState<
    Record<number, string>
  >(initialUserProps)

  React.useEffect(() => {
    setUserPropValues(initialUserProps)
  }, [initialUserProps])

  const [formDirty, setFormDirty] = React.useState(false)
  const userPropsDirty = React.useMemo(() => {
    const ids = new Set<number>([
      ...Object.keys(initialUserProps).map(Number),
      ...Object.keys(userPropValues).map(Number),
    ])
    for (const id of ids) {
      if ((userPropValues[id] ?? '') !== (initialUserProps[id] ?? '')) {
        return true
      }
    }
    return false
  }, [initialUserProps, userPropValues])

  React.useEffect(() => {
    props.setDirtyDrawer(formDirty || userPropsDirty)
  }, [formDirty, userPropsDirty, props.setDirtyDrawer])

  const noTravelPolicyOption = React.useMemo<TravelPolicyOption>(
    () => ({ value: null, label: 'No travel policy' }),
    [],
  )

  const formDefaults = React.useMemo(
    () => ({
      userId: props.user.userId,
      firstName: props.user.firstName,
      lastName: props.user.lastName,
      email: props.user.email,
      mobileNumber: props.user.contact?.mobileNumber,
      roles: props.user.roles,
      travelPolicyId: props.user.travelPolicyId ?? null,
    }),
    [props.user],
  )

  const defaultValuesRef = React.useRef(formDefaults)

  const clientTravelPolicyOptions = React.useMemo<TravelPolicyOption[]>(
    () =>
      (props.client.travelPolicies ?? []).map(
        ({ id, name }: { id: number; name: string }) => ({
          value: id,
          label: name,
        }),
      ),
    [props.client.travelPolicies],
  )

  const unknownTravelPolicyOption =
    React.useMemo<TravelPolicyOption | null>(() => {
      if (formDefaults.travelPolicyId == null) return null
      const exists = clientTravelPolicyOptions.some(
        (option) => option.value === formDefaults.travelPolicyId,
      )
      if (exists) return null
      return {
        value: formDefaults.travelPolicyId,
        label: `Unknown travel policy (ID: ${formDefaults.travelPolicyId})`,
      }
    }, [clientTravelPolicyOptions, formDefaults.travelPolicyId])

  const travelPolicyOptions = React.useMemo<TravelPolicyOption[]>(
    () =>
      [
        noTravelPolicyOption,
        ...clientTravelPolicyOptions,
        ...(unknownTravelPolicyOption ? [unknownTravelPolicyOption] : []),
      ] as TravelPolicyOption[],
    [
      clientTravelPolicyOptions,
      noTravelPolicyOption,
      unknownTravelPolicyOption,
    ],
  )

  const travelPolicyDefaultOption = React.useMemo(() => {
    if (formDefaults.travelPolicyId == null) return noTravelPolicyOption

    return (
      travelPolicyOptions.find(
        (option) => option.value === formDefaults.travelPolicyId,
      ) ??
      unknownTravelPolicyOption ??
      noTravelPolicyOption
    )
  }, [
    formDefaults.travelPolicyId,
    noTravelPolicyOption,
    travelPolicyOptions,
    unknownTravelPolicyOption,
  ])

  const [travelPolicySelection, setTravelPolicySelection] =
    React.useState<TravelPolicyOption>(travelPolicyDefaultOption)

  React.useEffect(() => {
    defaultValuesRef.current = formDefaults
    setFormDirty(false)
  }, [formDefaults])

  React.useEffect(() => {
    setTravelPolicySelection(travelPolicyDefaultOption)
  }, [travelPolicyDefaultOption])

  React.useEffect(() => {
    if (!formMethods.current) return
    const subscription = formMethods.current.watch((values) => {
      if (!values) return
      const defaults = defaultValuesRef.current
      const isDirty = Object.keys(defaults).some((key) => {
        return !areValuesEqual(
          values[key],
          defaults[key as keyof typeof defaults],
        )
      })
      setFormDirty(isDirty)
    })
    return () => subscription?.unsubscribe?.()
  }, [])

  const onUpdateRole = (_userId: string, selectedOptions: any[]) => {
    const roles = (selectedOptions || []).map((option: any) => option.value)
    console.log('roles', roles)
    formMethods.current?.setValue('roles', roles)
    setRoleError(roles.length ? null : 'Please select at least one role.')
  }

  const onUpdateTravelPolicy = (selectedOption: TravelPolicyOption | null) => {
    const nextSelection = selectedOption ?? noTravelPolicyOption
    setTravelPolicySelection(nextSelection)
    formMethods.current?.setValue(
      'travelPolicyId',
      nextSelection.value ?? null,
      { shouldDirty: true },
    )
  }

  const onSubmit = async (formData: any) => {
    const roleValues = formData.roles ?? []
    if (!roleValues.length) {
      setRoleError('Please select at least one role.')
      return
    }

    const changedUser = Object.keys(defaultValuesRef.current).reduce(
      (result, key) => {
        if (
          !areValuesEqual(
            formData[key],
            defaultValuesRef.current[
              key as keyof typeof defaultValuesRef.current
            ],
          )
        ) {
          result[key] = formData[key]
        }
        return result
      },
      {} as any,
    )

    if (!Object.keys(changedUser).length && !userPropsDirty) {
      notifyPopup({
        type: 'info',
        title: 'No updates',
        message: 'No changes were detected for this user.',
      })
      return
    }

    const submitData: any = {
      ...changedUser,
      clientId: props.client.id,
      delegates: mapDelegatesForPatch(props.user.delegates ?? []),
    }

    if (userPropsDirty) {
      const ids = new Set<number>([
        ...Object.keys(initialUserProps).map(Number),
        ...Object.keys(userPropValues).map(Number),
      ])
      submitData.userProperties = Array.from(ids)
        .map((id) => ({
          userPropertyId: id,
          value: userPropValues[id] ?? '',
        }))
        .filter(
          (entry) =>
            entry.value !== '' ||
            Object.prototype.hasOwnProperty.call(initialUserProps, entry.userPropertyId),
        )
    }
    submitData.travelPolicyId = Object.prototype.hasOwnProperty.call(
      changedUser,
      'travelPolicyId',
    )
      ? changedUser.travelPolicyId ?? null
      : props.user.travelPolicyId ?? null

    setSubmitPressed(true)
    try {
      await dispatch(
        updateUserById({
          updatedUser: submitData,
          userId: props.user.userId,
        }),
      )
        .unwrap()
        .then((then: any) => {
          notifyPopup({
            type: 'success',
            title: 'User updated',
            message: `${then.data.email} has now been updated.`,
          })
          props.closeDrawer()
        })
    } catch (error: any) {
      setSubmitPressed(false)
      notifyPopup({
        type: 'danger',
        title: 'Error',
        message: error.error.message,
      })
    }
  }

  return (
    <Box
      component="div"
      sx={{
        width: '100%',
        fontSize: '14px',
        backgroundColor: '#F8F8F8',
        color: '#8E8E8E',
        display: 'flex',
        flexShrink: 1,
        flexDirection: 'column',
        alignItems: 'center',
        paddingBottom: '20px',
      }}
    >
      <Grid
        container
        style={{
          width: '90%',
          display: 'block',
          textAlign: 'center',
          paddingTop: '20px',
        }}
      >
        <Form
          schema={updateUserSchema}
          ref={formMethods}
          onSubmit={onSubmit}
          submitText="Update User"
          defaultValues={formDefaults}
          isLoading={submitPressed}
        >
          <>
            <SectionHeader title="Update User" />
            <Typography sx={{ textAlign: 'left' }}>
              Only the user themselves can update their profile information. You
              can update the user&apos;s role and/or their travel policy - where
              applicable.
            </Typography>
            <Input name="firstName" label="First Name" disabled />
            <Input name="lastName" label="Last Name" disabled />
            <Input name="email" label="Email Address" disabled />
            <MuiPhoneForm
              name="mobileNumber"
              label="Mobile Number"
              fullWidth
              disabled
            />
            <Box sx={{ textAlign: 'left' }}>
              <Select
                isMulti
                placeholder="Select role(s)..."
                name="roles"
                defaultValue={filteredRoles
                  .filter((role) => formDefaults.roles?.includes(role.role))
                  .map((role) => ({
                    label: role.display,
                    value: role.role,
                  }))}
                onChange={(selectedOptions: any) => {
                  onUpdateRole(props.user.userId, selectedOptions || [])
                }}
                options={filteredRoles.map((role) => ({
                  label: role.display,
                  value: role.role,
                }))}
              />
              {roleError && (
                <Typography
                  variant="caption"
                  color="error"
                  sx={{ display: 'block', marginTop: '4px' }}
                >
                  {roleError}
                </Typography>
              )}
            </Box>
            <Box sx={{ marginTop: '20px', textAlign: 'left' }}>
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography variant="h6">Available Roles</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  {filteredRoles.map((role) => (
                    <Grid
                      container
                      key={role.role}
                      sx={{ marginBottom: '10px' }}
                    >
                      <Grid item xs={3}>
                        <strong>{role.display}</strong>:
                      </Grid>
                      <Grid item xs={9}>
                        {role.description}
                      </Grid>
                    </Grid>
                  ))}
                </AccordionDetails>
              </Accordion>
            </Box>

            <Box sx={{ marginTop: '20px', textAlign: 'left', width: '100%' }}>
              <Select
                name="travelPolicyId"
                placeholder="Select travel policy..."
                value={travelPolicySelection}
                onChange={onUpdateTravelPolicy}
                options={travelPolicyOptions}
                isClearable={travelPolicySelection.value != null}
              />
            </Box>
            {userProperties.length > 0 && (
              <Box sx={{ marginTop: '20px', textAlign: 'left' }}>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  Traveller properties
                </Typography>
                {userProperties.map((def) => {
                  const currentValue = userPropValues[def.id] ?? ''
                  const handleChange = (value: string) =>
                    setUserPropValues((prev) => ({ ...prev, [def.id]: value }))
                  const isArrayWithOptions =
                    def.userPropertyType === 'Array' &&
                    Array.isArray(def.values) &&
                    def.values.length > 0
                  if (isArrayWithOptions) {
                    return (
                      <FormControl
                        key={def.id}
                        fullWidth
                        size="small"
                        sx={{ mt: 2 }}
                      >
                        <InputLabel id={`user-property-${def.id}-label`}>
                          {def.title}
                          {def.isMandatory ? ' *' : ''}
                        </InputLabel>
                        <MuiSelect
                          labelId={`user-property-${def.id}-label`}
                          label={def.title}
                          value={currentValue}
                          onChange={(event) =>
                            handleChange(String(event.target.value))
                          }
                        >
                          <MenuItem value="">-</MenuItem>
                          {def.values!.map((v) => (
                            <MenuItem key={v} value={v}>
                              {v}
                            </MenuItem>
                          ))}
                        </MuiSelect>
                      </FormControl>
                    )
                  }
                  return (
                    <TextField
                      key={def.id}
                      fullWidth
                      size="small"
                      sx={{ mt: 2 }}
                      label={`${def.title}${def.isMandatory ? ' *' : ''}`}
                      value={currentValue}
                      onChange={(event) => handleChange(event.target.value)}
                    />
                  )
                })}
              </Box>
            )}
            <Box sx={{ marginTop: '20px', textAlign: 'left' }}>
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography variant="h6">Travel Policies</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableCell>Name</TableCell>
                        <TableCell>Policy Rule</TableCell>
                      </TableHead>
                      <TableBody>
                        {props.client.travelPolicies.map((policy: any) => (
                          <TableRow key={policy.id}>
                            <TableCell>{policy.name}</TableCell>
                            <TableCell>
                              <Accordion>
                                <AccordionSummary>
                                  Permitted Vehicles
                                </AccordionSummary>
                                <AccordionDetails>
                                  {policy.taxi.authorizedVehicles.map(
                                    (v: string) => (
                                      <Box key={v}>
                                        {String(v).replaceAll('_', ' ')}{' '}
                                      </Box>
                                    ),
                                  )}
                                </AccordionDetails>
                              </Accordion>
                              <Accordion>
                                <AccordionSummary>
                                  Permitted Booking Times
                                </AccordionSummary>
                                <AccordionDetails>
                                  {Object.entries(policy.taxi.validJourneyTimes)
                                    .length > 0
                                    ? Object.entries(
                                        policy.taxi.validJourneyTimes,
                                      ).map(([key, value]: [string, any]) => (
                                        <Box key={key}>{value} </Box>
                                      ))
                                    : 'Any time'}
                                </AccordionDetails>
                              </Accordion>
                              <Accordion>
                                <AccordionSummary>
                                  Spend Limits
                                </AccordionSummary>
                                <AccordionDetails>
                                  {Object.entries(policy.taxi).map(
                                    (k: any, idx: number) => {
                                      if (
                                        Object.keys(
                                          travelPolicySpendAttributes,
                                        ).includes(k[0])
                                      )
                                        return (
                                          <Box
                                            key={idx}
                                            sx={{
                                              display: 'flex',
                                              justifyContent: 'space-between',
                                            }}
                                          >
                                            <Box>
                                              {Object.entries(
                                                travelPolicySpendAttributes,
                                              ).find(
                                                (t: any) => t[0] === k[0],
                                              )?.[1] ?? ''}
                                            </Box>
                                            <Box>{`${k[1]}`}</Box>
                                          </Box>
                                        )
                                    },
                                  )}
                                </AccordionDetails>
                              </Accordion>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </AccordionDetails>
              </Accordion>
            </Box>
          </>
        </Form>
      </Grid>
    </Box>
  )
}

export default UpdateUser
